import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPendingReports } from '../api/reportApi';
import Pagination from '../components/Pagination';
import { formatDateTime, formatPatientName, normalizeUiLocale } from '../utils/locale';

export const shouldShowGenerationRetry = (report = {}) => (
  Number(report.generation_attempts) > 1 && Number(report.generation_max_attempts) > 0
);

const ReportList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filterColumn, setFilterColumn] = useState('id');
  const [filterValue, setFilterValue] = useState('');
  const [filteredReports, setFilteredReports] = useState([]);
  const [allReports, setAllReports] = useState([]);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const notificationTimerRef = useRef(null);
  const shouldReconnectRef = useRef(true);
  const latestQueryRef = useRef({
    currentPage: 1,
    pageSize: 10,
    filterColumn: 'id',
    filterValue: ''
  });
  const connectWebSocketRef = useRef(null);
  const { t, i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const location = useLocation();

  const filterOptions = [
    { value: 'id', label: t('reports.reportId') },
    { value: 'visit_id', label: t('reports.visitId') },
    { value: 'patient_name', label: t('reports.patientName') },
    { value: 'clinic_name', label: t('reports.clinicName') },
    { value: 'report_type', label: t('reports.reportType') },
    { value: 'workflow', label: t('reports.workflow') },
    { value: 'stage', label: t('reports.stage') },
    { value: 'status', label: t('reports.status') },
    { value: 'visit_type', label: t('reports.visitType') }
  ];

  const filterReports = useCallback((data, column, value) => {
    if (!value || value.trim() === '') {
      return data;
    }

    return data.filter((report) => {
      const fieldValue = report[column];
      if (fieldValue === null || fieldValue === undefined) {
        return false;
      }

      return String(fieldValue).toLowerCase().includes(value.toLowerCase().trim());
    });
  }, []);

  const paginateData = useCallback((data, page, size) => {
    const safeTotalPages = Math.max(1, Math.ceil(data.length / size));
    const safePage = Math.min(page, safeTotalPages);
    const startIndex = (safePage - 1) * size;
    const endIndex = startIndex + size;

    return {
      currentPage: safePage,
      totalPages: safeTotalPages,
      totalCount: data.length,
      rows: data.slice(startIndex, endIndex)
    };
  }, []);

  const getPresetFilterFromSearch = useCallback((search) => {
    const params = new URLSearchParams(search);
    const visitID = params.get('visit_id');
    if (visitID) {
      return {
        column: 'visit_id',
        value: visitID
      };
    }

    return {
      column: 'id',
      value: ''
    };
  }, []);

  const buildReviewedVisitLink = (visitId) => `/reviewed-reports?visit_id=${visitId}`;

  const fetchReports = useCallback(async (page, size, nextFilterColumn, nextFilterValue) => {
    try {
      setLoading(true);
      const allData = await getPendingReports(1, 1000);
      const data = allData || [];
      const filtered = filterReports(data, nextFilterColumn, nextFilterValue);
      const pageData = paginateData(filtered, page, size);

      setAllReports(data);
      setFilteredReports(filtered);
      setReports(pageData.rows);
      setCurrentPage(pageData.currentPage);
      setPageSize(size);
      setTotalCount(pageData.totalCount);
      setTotalPages(pageData.totalPages);
      setFilterColumn(nextFilterColumn);
      setFilterValue(nextFilterValue);
      setError('');
    } catch (requestError) {
      console.error('获取报告列表失败:', requestError);
      setError(`${t('reports.fetchError')}: ${requestError.response?.data?.error || t('common.unknownError')}`);
    } finally {
      setLoading(false);
    }
  }, [filterReports, paginateData, t]);

  const handleSearch = () => {
    const filtered = filterReports(allReports, filterColumn, filterValue);
    const pageData = paginateData(filtered, 1, pageSize);

    setFilteredReports(filtered);
    setReports(pageData.rows);
    setCurrentPage(pageData.currentPage);
    setTotalCount(pageData.totalCount);
    setTotalPages(pageData.totalPages);
  };

  const handleReset = () => {
    fetchReports(1, pageSize, filterColumn, '');
  };

  const handlePageChange = (page) => {
    const pageData = paginateData(filteredReports, page, pageSize);
    setReports(pageData.rows);
    setCurrentPage(pageData.currentPage);
  };

  const handlePageSizeChange = (nextPageSize) => {
    const pageData = paginateData(filteredReports, 1, nextPageSize);
    setReports(pageData.rows);
    setPageSize(nextPageSize);
    setCurrentPage(pageData.currentPage);
    setTotalPages(pageData.totalPages);
  };

  const showNotification = useCallback((message) => {
    setNotification(message);
    window.clearTimeout(notificationTimerRef.current);
    notificationTimerRef.current = window.setTimeout(() => {
      setNotification('');
    }, 3000);
  }, []);

  useEffect(() => {
    latestQueryRef.current = {
      currentPage,
      pageSize,
      filterColumn,
      filterValue
    };
  }, [currentPage, pageSize, filterColumn, filterValue]);

  const connectWebSocket = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'doctor' }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_report' || data.type === 'report_generation_updated') {
          if (data.type === 'new_report') {
            showNotification(t('reports.newReportNotification'));
          } else if (data.report_status === 'generation_failed') {
            showNotification(t('reports.generationFailedRetry'));
          }
          const {
            currentPage: latestPage,
            pageSize: latestPageSize,
            filterColumn: latestFilterColumn,
            filterValue: latestFilterValue
          } = latestQueryRef.current;
          fetchReports(latestPage, latestPageSize, latestFilterColumn, latestFilterValue);
        }
      } catch (parseError) {
        console.error('处理WebSocket消息失败:', parseError);
      }
    };

    ws.onclose = () => {
      if (!shouldReconnectRef.current) {
        return;
      }

      reconnectTimerRef.current = window.setTimeout(() => {
        connectWebSocketRef.current?.();
      }, 5000);
    };

    ws.onerror = (socketError) => {
      console.error('WebSocket错误:', socketError);
    };
  }, [fetchReports, showNotification, t]);

  useEffect(() => {
    connectWebSocketRef.current = connectWebSocket;
  }, [connectWebSocket]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connectWebSocket();

    return () => {
      shouldReconnectRef.current = false;
      window.clearTimeout(reconnectTimerRef.current);
      window.clearTimeout(notificationTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket]);

  useEffect(() => {
    const preset = getPresetFilterFromSearch(location.search);
    fetchReports(1, 10, preset.column, preset.value);
  }, [location.search, getPresetFilterFromSearch, fetchReports]);

  const formatReportDate = (dateString) => formatDateTime(dateString, locale);
  const getReportPatientName = (report) => formatPatientName(
    report,
    locale,
    report.patient_name || t('common.unknown')
  );

  const getReportTypeDisplay = (reportType) => {
    if (!reportType) {
      return t('reports.unknownType');
    }

    const type = reportType.toLowerCase();

    if (type === 'proposal' || type === '建议' || type === '初步') {
      return t('reports.proposalReport');
    }

    if (type === 'final' || type === '最终' || type === '终结') {
      return t('reports.finalReport');
    }

    return reportType.charAt(0).toUpperCase() + reportType.slice(1);
  };

  const getVisitTypeDisplay = (visitType) => {
    if (visitType === 'follow_up') {
      return t('reports.visitTypeFollowUp');
    }
    if (visitType === 'initial') {
      return t('reports.visitTypeInitial');
    }
    if (visitType === 'checkup') {
      return t('reports.visitTypeCheckup');
    }
    return t('reports.visitTypeUnknown');
  };

  const getWorkflowDisplay = (report) => (
    (report.workflow === 'single_review' || report.report_flow_version === 2)
      ? t('reports.singleReviewFlow')
      : t('reports.legacyFlow')
  );

  const getPendingStatusDisplay = (status) => {
    if (status === 'generating') return t('reports.generating');
    if (status === 'generation_failed') return t('reports.generation_failed');
    return t('reports.pending');
  };

  const getRelatedVisitsDisplay = (relatedVisitIds) => {
    if (!relatedVisitIds || relatedVisitIds.length === 0) {
      return t('reports.noLinkedSourceVisits');
    }

    return t('reports.relatedVisitsValue', {
      visits: relatedVisitIds.map((visitId) => `#${visitId}`).join(' / ')
    });
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="page-title-block">
          <h1 className="page-title">{t('reports.pendingReportsList')}</h1>
        </div>

        <div className="page-actions">
          <button type="button" className="theme-button theme-button--secondary" onClick={() => fetchReports(1, pageSize, filterColumn, filterValue)}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {notification && (
        <div className="status-banner status-banner--success">
          <span>{notification}</span>
        </div>
      )}

      {error && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      <section className="surface-panel">
        <div className="surface-panel__body stack">
          <div className="toolbar-cluster">
            <span className="status-pill status-pill--success">
              {t('reports.pendingCount', { count: totalCount })}
            </span>
            <span className="status-pill status-pill--neutral">
              {t('reports.pageCount', { current: currentPage, total: totalPages })}
            </span>
          </div>

          <div className="filter-row">
            <div className="field">
              <label className="field__label" htmlFor="pending-filter-column">
                {t('common.filterBy')}
              </label>
              <select
                id="pending-filter-column"
                className="theme-select"
                value={filterColumn}
                onChange={(event) => setFilterColumn(event.target.value)}
              >
                {filterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="pending-filter-value">
                {t('common.search')}
              </label>
              <input
                id="pending-filter-value"
                className="theme-input"
                type="text"
                value={filterValue}
                onChange={(event) => setFilterValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
                placeholder={t('common.enterSearchValue')}
              />
            </div>

            <button type="button" className="theme-button theme-button--primary" onClick={handleSearch}>
              {t('common.search')}
            </button>
            <button type="button" className="theme-button theme-button--ghost" onClick={handleReset}>
              {t('common.reset')}
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-orb" />
              <span>{t('common.loading')}</span>
            </div>
          ) : reports.length === 0 ? (
            <div className="empty-state">
              <h3 className="auth-form__title">{t('reports.noReports')}</h3>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('reports.reportId')}</th>
                    <th>{t('reports.patientName')}</th>
                    <th>{t('reports.clinicName')}</th>
                    <th>{t('reports.reportType')}</th>
                    <th>{t('reports.status')}</th>
                    <th>{t('reports.createdAt')}</th>
                    <th>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id}>
                      <td>
                        <div className="table-title">#{report.id}</div>
                        <div className="table-meta">{t('reports.visitId')}: {report.visit_id}</div>
                        <div className="table-meta">{getRelatedVisitsDisplay(report.related_visit_ids)}</div>
                        {report.related_visit_ids?.length > 0 && (
                          <div className="visit-chip-list">
                            {report.related_visit_ids.map((visitId) => (
                              <Link key={`${report.id}-${visitId}`} to={buildReviewedVisitLink(visitId)} className="visit-chip">
                                #{visitId}
                              </Link>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        {report.patient_id ? (
                          <Link className="table-link-button table-link-button--inline" to={`/patients/${report.patient_id}`}>
                            <bdi dir="auto">{getReportPatientName(report)}</bdi>
                          </Link>
                        ) : (
                          <div className="table-title"><bdi dir="auto">{getReportPatientName(report)}</bdi></div>
                        )}
                      </td>
                      <td>
                        <div className="table-title">{report.clinic_name || t('reports.notSpecified')}</div>
                        <div className="table-meta">{t('reports.clinicId')}: {report.clinic_id || t('reports.notSpecified')}</div>
                      </td>
                      <td>
                        <span className="status-pill status-pill--warning">
                          {getReportTypeDisplay(report.report_type)}
                        </span>
                        <div style={{ marginTop: '0.5rem' }}>
                          <span className={`status-pill ${report.visit_type === 'follow_up' ? 'status-pill--success' : 'status-pill--neutral'}`}>
                            {getVisitTypeDisplay(report.visit_type)}
                          </span>
                        </div>
                        <div className="table-meta" style={{ marginTop: '0.5rem' }}>
                          {getWorkflowDisplay(report)} · {getReportTypeDisplay(report.stage || report.report_type)}
                          {report.revision_number ? ` · ${t('reports.revision')} ${report.revision_number}` : ''}
                        </div>
                      </td>
                      <td>
                        <span className={`status-pill ${report.status === 'generation_failed' ? 'status-pill--danger' : report.status === 'generating' ? 'status-pill--cool' : 'status-pill--warning'}`}>
                          {getPendingStatusDisplay(report.status)}
                        </span>
                        {shouldShowGenerationRetry(report) && (
                          <div className="table-meta" style={{ marginTop: '0.5rem' }}>
                            {t('reports.generationRetryAttempt', {
                              current: report.generation_attempts,
                              max: report.generation_max_attempts
                            })}
                          </div>
                        )}
                        {report.generation_error && (
                          <div className="table-meta" title={report.generation_error} style={{ marginTop: '0.35rem', maxWidth: '18rem' }}>
                            {report.generation_error}
                          </div>
                        )}
                      </td>
                      <td>{formatReportDate(report.created_at)}</td>
                      <td>
                        <div className="table-actions">
                          <Link
                            to={`/reports/${report.id}/review`}
                            className={`theme-button ${report.status === 'pending' ? 'theme-button--primary' : 'theme-button--ghost'}`}
                          >
                            {report.status === 'pending'
                              ? t('reports.review')
                              : report.status === 'generation_failed'
                                ? t('reports.retryGeneration')
                                : t('reports.viewGenerationStatus')}
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && reports.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </section>
    </div>
  );
};

export default ReportList;
